import * as React from 'react';
import { WorklynxIcon } from '@/components/icons/WorklynxIcon';
import { dateInputToIso, isoToDateInput, formatDisplayDate } from '@/lib/dateDisplay';
import { toDateOnlyString } from '@/lib/utils';

export interface DateInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * Show the calendar as part of the field rather than as a popup over the page. For a
   * dialog whose whole job is picking a date: the popup is taller than such a dialog and
   * either gets clipped by it or hangs outside it.
   */
  inlineCalendar?: boolean;
}

/** Controlled ISO value outside; day-first text and calendar inside. */
export const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(
  (
    { value, defaultValue, onChange, onBlur, type, min, max, className, inlineCalendar, ...props },
    forwardedRef,
  ) => {
    const withTime = type === 'datetime-local';
    const [draft, setDraft] = React.useState(() =>
      isoToDateInput(String(value ?? defaultValue ?? ''), withTime),
    );
    const [open, setOpen] = React.useState(false);
    const calendarShown = inlineCalendar || open;
    const [month, setMonth] = React.useState(
      () => new Date(`${String(value || toDateOnlyString(new Date())).slice(0, 10)}T12:00:00`),
    );
    const root = React.useRef<HTMLDivElement>(null);
    const input = React.useRef<HTMLInputElement | null>(null);
    const emitted = React.useRef<string | undefined>(undefined);
    const parsed = dateInputToIso(draft, withTime);
    const error =
      parsed === null
        ? `Enter ${withTime ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY'}.`
        : parsed && min && parsed < String(min)
          ? `Use ${isoToDateInput(String(min), withTime)} or later.`
          : parsed && max && parsed > String(max)
            ? `Use ${isoToDateInput(String(max), withTime)} or earlier.`
            : '';

    React.useEffect(() => {
      if (value !== undefined && String(value) !== emitted.current)
        setDraft(isoToDateInput(String(value), withTime));
      emitted.current = undefined;
    }, [value, withTime]);
    React.useEffect(() => {
      input.current?.setCustomValidity(error);
    }, [error]);
    React.useEffect(() => {
      if (!open) return;
      const close = (event: PointerEvent) => {
        if (!root.current?.contains(event.target as Node)) setOpen(false);
      };
      document.addEventListener('pointerdown', close);
      return () => document.removeEventListener('pointerdown', close);
    }, [open]);

    function change(text: string, event: React.SyntheticEvent) {
      setDraft(text);
      const next = dateInputToIso(text, withTime) ?? '';
      emitted.current = next;
      // Existing callers consume target.value as ISO, including Controller fields.
      onChange?.({
        ...event,
        target: { ...input.current, name: props.name, value: next },
        currentTarget: { ...input.current, name: props.name, value: next },
      } as React.ChangeEvent<HTMLInputElement>);
    }
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const offset = (first.getDay() + 6) % 7;
    const days = Array.from(
      { length: 42 },
      (_, index) => new Date(month.getFullYear(), month.getMonth(), index - offset + 1),
    );
    // Computed from the browser's own clock, so "today" is the user's today rather than
    // the server's - someone in Perth opening a picker at 1am should not see yesterday
    // ringed.
    const todayIso = toDateOnlyString(new Date());
    const format = withTime ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY';
    return (
      <div
        ref={root}
        className="relative"
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setOpen(false);
            input.current?.focus();
          }
        }}
      >
        <input
          {...props}
          ref={(node) => {
            input.current = node;
            if (typeof forwardedRef === 'function') forwardedRef(node);
            else if (forwardedRef) forwardedRef.current = node;
          }}
          type="text"
          value={draft}
          placeholder={format}
          aria-label={props['aria-label'] ?? props.name ?? 'Date'}
          aria-invalid={Boolean(error) || props['aria-invalid']}
          className={`${className ?? ''} ${inlineCalendar ? '' : 'pr-10'}`}
          autoComplete="off"
          maxLength={withTime ? 16 : 10}
          onChange={(event) => change(event.target.value, event)}
          onBlur={(event) => {
            onBlur?.({
              ...event,
              target: { ...event.target, name: props.name, value: parsed ?? '' },
            } as React.FocusEvent<HTMLInputElement>);
          }}
        />
        {!inlineCalendar && (
        <button
          type="button"
          className="absolute right-2 top-2 rounded p-1 text-muted hover:text-foreground focus-visible:outline focus-visible:outline-2"
          aria-label={`Choose date${props['aria-label'] ? ` for ${props['aria-label']}` : ''}`}
          aria-expanded={open}
          disabled={props.disabled || props.readOnly}
          onClick={() => {
            if (parsed) setMonth(new Date(`${parsed.slice(0, 10)}T12:00:00`));
            setOpen(!open);
          }}
        >
          <WorklynxIcon name="roster" size={16} />
        </button>
        )}
        {calendarShown && (
          <div
            role="dialog"
            aria-label="Choose date"
            className={
              inlineCalendar
                ? 'mt-2 w-full rounded-lg border border-brand-border bg-card p-3'
                : 'absolute left-0 top-full z-[100] mt-1 w-72 max-w-[85vw] rounded-lg border border-brand-border bg-card p-3 shadow-xl'
            }
          >
            <div className="mb-3 flex items-center justify-between gap-1">
              <button
                type="button"
                aria-label="Previous month"
                onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
              >
                <WorklynxIcon name="chevron-left" size={20} />
              </button>
              <select
                aria-label="Month"
                className="min-w-0 bg-card text-sm"
                value={month.getMonth()}
                onChange={(e) => setMonth(new Date(month.getFullYear(), Number(e.target.value), 1))}
              >
                {Array.from({ length: 12 }, (_, m) => (
                  <option key={m} value={m}>
                    {new Date(2000, m, 1).toLocaleDateString('en-GB', { month: 'long' })}
                  </option>
                ))}
              </select>
              <input
                aria-label="Year"
                type="number"
                className="w-16 bg-card text-sm"
                min="1"
                max="9999"
                value={month.getFullYear()}
                onChange={(e) => {
                  const y = Number(e.target.value);
                  if (y >= 100 && y <= 9999) setMonth(new Date(y, month.getMonth(), 1));
                }}
              />
              <button
                type="button"
                aria-label="Next month"
                onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
              >
                <WorklynxIcon name="chevron-right" size={20} />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-xs">
              {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((day) => (
                <span key={day} className="py-1 text-muted">
                  {day}
                </span>
              ))}
              {days.map((day) => {
                const iso = toDateOnlyString(day);
                const disabled = Boolean(
                  (min && iso < String(min).slice(0, 10)) ||
                  (max && iso > String(max).slice(0, 10)),
                );
                return (
                  <button
                    key={iso}
                    type="button"
                    aria-label={formatDisplayDate(day)}
                    aria-pressed={parsed?.slice(0, 10) === iso}
                    aria-current={iso === todayIso ? 'date' : undefined}
                    disabled={disabled}
                    // Today is ringed rather than filled, so it still reads when it is
                    // also the selected day - which is the common case on a new shift.
                    className={`rounded py-2 hover:bg-brand-soft disabled:opacity-30 ${month.getMonth() === day.getMonth() ? 'text-foreground' : 'text-muted'} ${parsed?.slice(0, 10) === iso ? 'bg-brand-soft font-bold' : ''} ${iso === todayIso ? 'ring-brand/70 font-semibold ring-1 ring-inset' : ''}`}
                    onClick={(event) => {
                      change(
                        isoToDateInput(
                          iso + (withTime ? `T${parsed?.slice(11, 16) || '00:00'}` : ''),
                          withTime,
                        ),
                        event,
                      );
                      setOpen(false);
                      input.current?.focus();
                    }}
                  >
                    {day.getDate()}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {error && (
          <span className="mt-1 block text-xs text-danger" role="alert">
            {error}
          </span>
        )}
      </div>
    );
  },
);
DateInput.displayName = 'DateInput';
